export interface Workspace {
  id: string;
  name: string;
  slug: string;
  timezone: string;
  defaultLocale: string;
  brandVoice: string | null;
  brandNotes: string | null;
  archivedAt: number | null;
  createdAt: number;
  updatedAt: number;
}

export interface Member {
  id: string;
  workspaceId: string;
  email: string;
  name: string | null;
  role: "approver" | "viewer";
  locale: string;
  createdAt: number;
}

export interface Channel {
  id: string;
  workspaceId: string;
  platform: string;
  externalId: string;
  displayName: string;
  handle: string | null;
  status: string;
  tokenExpiresAt: number | null;
  lastError: string | null;
}

export interface AiProviderRow {
  id: string;
  workspaceId: string | null;
  provider: "anthropic" | "xai" | "openai" | "gemini";
  label: string;
  apiKeyHint: string;
  capabilities: string[];
  textModel: string | null;
  imageModel: string | null;
  videoModel: string | null;
  baseUrl: string | null;
  status: "untested" | "ok" | "error";
  lastError: string | null;
  lastCheckedAt: number | null;
  createdAt: number;
}

export interface ProviderCatalogEntry {
  provider: string;
  capabilities: string[];
  defaults: { text: string; image?: string; video?: string };
}

export const LOCALE_OPTIONS = [
  { value: "en", label: "English" },
  { value: "de", label: "German" },
  { value: "de-CH", label: "German (Switzerland)" },
  { value: "nb", label: "Norwegian" },
  { value: "fr", label: "French" },
  { value: "it", label: "Italian" },
];

export const TIMEZONE_OPTIONS = [
  "Europe/Oslo",
  "Europe/Berlin",
  "Europe/Zurich",
  "Europe/London",
  "America/New_York",
  "America/Chicago",
  "America/Denver",
  "America/Los_Angeles",
  "UTC",
];

export const PROVIDER_LABELS: Record<string, string> = {
  anthropic: "Anthropic Claude",
  xai: "xAI Grok",
  openai: "OpenAI",
  gemini: "Google Gemini",
};

export interface Plan {
  id: string;
  workspaceId: string;
  title: string;
  periodStart: number;
  periodEnd: number;
  status: string;
  shareToken: string;
  sentAt: number | null;
  approvedAt: number | null;
  brief: string | null;
  createdAt: number;
  postCount?: number;
}

export interface LengthWarning {
  platform: string;
  chars: number;
  limit: number;
}

export interface Variant {
  id: string;
  postId: string;
  channelId: string;
  platform: string;
  body: string;
  title: string | null;
  hashtags: string[];
  mediaIds: string[];
  firstComment: string | null;
  options: Record<string, unknown>;
  status: string;
  charCount: number;
  target: [number, number] | null;
  hardLimit?: number | null;
  requiresMedia?: boolean;
  warning: LengthWarning | null;
}

export interface FeedbackRow {
  id: string;
  postId: string | null;
  authorKind: string;
  authorLabel: string;
  body: string;
  resolvedAt: number | null;
  createdAt: number;
}

export interface Post {
  id: string;
  workspaceId: string;
  planId: string | null;
  scheduledAt: number;
  locale: string;
  brief: string | null;
  status: string;
  revision: number;
  dayKey: string;
  variants: Variant[];
  feedback: FeedbackRow[];
}

export interface PlanApproval {
  scope: string;
  scopeKey: string | null;
  decision: string;
  actorLabel: string;
  note: string | null;
  createdAt: number;
}

export interface PlanDetail {
  plan: Plan;
  approvals?: PlanApproval[];
  workspace: { id: string; name: string; timezone: string; defaultLocale: string };
  posts: Post[];
  failures?: string[];
}

export interface TopicSuggestion {
  brief: string;
  rationale: string;
}

export const PLATFORM_LABELS: Record<string, string> = {
  linkedin: "LinkedIn",
  linkedin_page: "LinkedIn Page",
  facebook: "Facebook",
  instagram: "Instagram",
  x: "X",
  tiktok: "TikTok",
  youtube: "YouTube",
};

export const PLATFORM_OPTIONS = Object.keys(PLATFORM_LABELS);

export const WEEKDAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

/** ISO date key (YYYY-MM-DD) helpers, matching the server's day keys. */
export function addDays(key: string, days: number): string {
  const [y, m, d] = key.split("-").map(Number);
  return new Date(Date.UTC(y!, m! - 1, d! + days)).toISOString().slice(0, 10);
}

export function startOfWeek(key: string): string {
  const [y, m, d] = key.split("-").map(Number);
  const weekday = (new Date(Date.UTC(y!, m! - 1, d!)).getUTCDay() + 6) % 7;
  return addDays(key, -weekday);
}

export function todayKey(): string {
  return new Date().toISOString().slice(0, 10);
}

/** Formats a day key as "Mon 14 Sep" without pulling in a date library. */
export function formatDayKey(key: string): string {
  const [y, m, d] = key.split("-").map(Number);
  const date = new Date(Date.UTC(y!, m! - 1, d!));
  return date.toLocaleDateString(undefined, {
    weekday: "short",
    day: "numeric",
    month: "short",
    timeZone: "UTC",
  });
}

/** Time of day in a given IANA timezone, as HH:MM. */
export function formatTimeIn(unixSeconds: number, timeZone: string): string {
  return new Intl.DateTimeFormat(undefined, {
    timeZone,
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  }).format(new Date(unixSeconds * 1000));
}

export interface ProviderInfo {
  key: string;
  label: string;
  configured: boolean;
  multiChannel: boolean;
}

export interface QueueItem {
  variantId: string;
  postId: string;
  platform: string;
  status: string;
  scheduledAt: number;
  publishedAt: number | null;
  externalUrl: string | null;
  lastError: string | null;
  attempts: number;
  preview: string;
  channelName: string;
  channelStatus: string;
  workspaceName: string;
  workspaceId: string;
  timezone: string;
  canPublish: boolean;
}

/** Which OAuth flow a platform belongs to. Meta covers Facebook and Instagram. */
export function providerKeyFor(platform: string): string {
  if (platform === "facebook" || platform === "instagram") return "meta";
  if (platform === "linkedin_page") return "linkedin";
  return platform;
}

export function formatDateTimeIn(unixSeconds: number, timeZone: string): string {
  return new Intl.DateTimeFormat(undefined, {
    timeZone,
    day: "numeric",
    month: "short",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  }).format(new Date(unixSeconds * 1000));
}
